import React, { useEffect, useState, useRef } from "react";
import {
  FaSearch,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { MdClose, MdOutlineSecurityUpdateGood } from "react-icons/md";
import { CiMenuKebab } from "react-icons/ci";
import { AiOutlineDelete } from "react-icons/ai";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { ImCross } from "react-icons/im";
import {
  deleteEnquiry,
  getAllEnquiry,
  updateEnquiry,
} from "../apis/enquiryApi";

const Enquiry = () => {
  const [datas, setDatas] = useState([]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [statusOpenId, setStatusOpenId] = useState(null);
  const [formData, setFormData] = useState({
    status: "not yet",
    feedback: "",
    followDate: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [currentImages, setCurrentImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  /* ---------------- Fetch Enquiries ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllEnquiry();
        if (res?.success) setDatas(res.data);
      } catch (err) {
        toast.error("Failed to load enquiries");
      }
    };
    fetchData();
  }, []);

  /* ---------------- Outside Click ---------------- */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------------- Handlers ---------------- */
  const handleMenuClick = (id, item) => {
    setMenuOpenId(id);
    setStatusOpenId(id);
    setFormData({
      status: item.status || "not yet",
      feedback: item.feedback || "",
    });
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${name}" permanently?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteEnquiry(id);
        setDatas(datas.filter((d) => d._id !== id));
        Swal.fire("Deleted!", "Enquiry removed", "success");
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const res = await updateEnquiry(statusOpenId, formData);
      if (res?.success) {
        toast.success("Status updated");
        const refresh = await getAllEnquiry();
        setDatas(refresh.data);
        setStatusOpenId(null);
        if (formData.status === "approved") navigate("/leads");
      }
    } catch {
      toast.error("Update failed");
    }
  };

  /* ---------------- Pagination ---------------- */
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = datas.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(datas.length / itemsPerPage);

  /* ---------------- Media Viewer ---------------- */
  const openSlideshow = (images) => {
    setCurrentImages(images);
    setCurrentIndex(0);
    setIsModalOpen(true);
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <h1 className="text-2xl font-semibold">Enquiries</h1>

        <div className="relative w-64">
          <input
            type="text"
            placeholder="Search..."
            className="w-full border rounded-lg px-4 py-2 text-sm"
          />
          <FaSearch className="absolute right-3 top-3 text-gray-400" />
        </div>

        <Link to="/addenquiry">
          <button className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
            <FaPlus /> Add Enquiry
          </button>
        </Link>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-red-600 text-white">
            <tr>
              {[
                "S.No",
                "Name",
                "Address",
                "Land",
                "Source",
                "Media",
                "Date",
                "Status",
                "Action",
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-xs text-left uppercase">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {currentItems.length === 0 && (
              <tr>
                <td colSpan="9" className="py-10 text-center text-gray-500">
                  No enquiries found
                </td>
              </tr>
            )}

            {currentItems.map((item, i) => (
              <tr
                key={item._id}
                className={`${
                  i % 2 === 0 ? "bg-white" : "bg-gray-50"
                } hover:bg-gray-100 transition`}
              >
                <td className="px-4 py-3 text-sm">{indexOfFirst + i + 1}</td>

                <td className="px-4 py-3 text-sm">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs">{item.phone}</p>
                  <p className="text-xs">{item.email}</p>
                </td>

                <td className="px-4 py-3 text-sm">{item.address}</td>
                <td className="px-4 py-3 text-sm">{item.landSize}</td>
                <td className="px-4 py-3 text-sm">{item.source}</td>

                <td className="px-4 py-3 text-sm">
                  {item.medias?.length > 0 && (
                    <span
                      onClick={() => openSlideshow(item.medias)}
                      className="text-red-600 cursor-pointer hover:underline"
                    >
                      View
                    </span>
                  )}
                </td>

                <td className="px-4 py-3 text-sm">
                  {new Date(item.date).toLocaleDateString("en-GB")}
                </td>

                <td className="px-4 py-3 text-sm capitalize">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700
                      ${
                        item.status === "approved" &&
                        "bg-green-100 text-green-700"
                      }
                      ${item.status === "rejected" && "bg-red-100 text-red-700"}
                      ${
                        item.status === "followup" &&
                        "bg-blue-100 text-blue-700"
                      }
                    `}
                  >
                    {item.status || "Not Yet"}
                  </span>
                </td>

                <td>
                  <div className=" flex gap-2 justify-center items-center ">
                    <button
                      onClick={() => handleDelete(item._id, item.name)}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100 transition"
                    >
                      <AiOutlineDelete /> Delete
                    </button>
                    <button
                      onClick={() => setStatusOpenId(item._id)}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition"
                    >
                      <MdOutlineSecurityUpdateGood /> Status
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2 mt-6">
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === i + 1
                ? "bg-red-600 text-white"
                : "border hover:bg-gray-100"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      {/* Status Update Modal */}
      {statusOpenId &&
        datas
          .filter((data) => data._id === statusOpenId)
          .map((data) => (
            <div
              key={data._id}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            >
              <div className="w-[400px] bg-white border rounded-lg shadow-xl px-5 py-6 relative">
                {/* Close Button */}
                <button
                  onClick={() => setStatusOpenId(null)}
                  className="absolute top-3 right-3 text-red-600 hover:text-red-800"
                >
                  <ImCross />
                </button>

                {/* User Info */}
                <div className="flex justify-between bg-gray-100 p-3 rounded-md">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {data.name}
                    </p>
                    <p className="text-sm text-gray-600">{data.phone}</p>
                    <p className="text-sm text-gray-600">{data.email}</p>
                  </div>
                </div>

                {/* Status Options */}
                <div className="mt-4">
                  <h2 className="text-lg font-bold text-black">
                    Update Status
                  </h2>
                </div>
                <div className="flex justify-between gap-10 mt-4">
                  {["approved", "followup", "rejected"].map((status) => (
                    <div key={status} className="flex space-x-2">
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={formData.status === status}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                        className={`w-4 h-4 focus:ring-${
                          status === "approved"
                            ? "green"
                            : status === "followup"
                            ? "blue"
                            : "red"
                        }-500 text-${status}-600`}
                      />
                      <span
                        className={`font-semibold cursor-pointer text-${
                          status === "approved"
                            ? "green"
                            : status === "followup"
                            ? "blue"
                            : "red"
                        }-600 hover:underline`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Feedback */}
                <div className="mt-5">
                  <textarea
                    placeholder="Enter feedback"
                    value={formData.feedback}
                    onChange={(e) =>
                      setFormData({ ...formData, feedback: e.target.value })
                    }
                    className="w-full border border-gray-300 p-3 rounded-md text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>

                {/* Date */}
                <div className="mt-5">
                  <input
                    type="date"
                    value={formData.followDate}
                    onChange={(e) =>
                      setFormData({ ...formData, followDate: e.target.value })
                    }
                    className="w-full border border-gray-300 p-3 rounded-md text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="w-full mt-6">
                  <button
                    onClick={handleSubmit}
                    className="rounded-md px-4 py-2 w-full text-white bg-red-600 hover:bg-red-700 transition"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          ))}

      {/* Media Slideshow Modal */}
      {isModalOpen && currentImages.length > 0 && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/50 backdrop-blur-sm z-50 transition-opacity">
          <div className="relative bg-white p-6 rounded-xl shadow-2xl w-[480px] h-auto flex flex-col items-center">
            {/* Close Button */}
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-red-600 transition-colors"
              onClick={() => setIsModalOpen(false)}
            >
              <MdClose size={24} />
            </button>

            {/* Main Image */}
            <div className="flex justify-center mt-6">
              <img
                src={currentImages[currentIndex]}
                alt="Slideshow"
                className="w-[350px] h-[350px] rounded-xl object-cover shadow-lg transition-transform duration-300 hover:scale-105"
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between w-full px-6 mt-4">
              <button
                className="bg-gray-800 text-white p-3 rounded-full hover:bg-gray-700 shadow-md transition-all"
                onClick={() =>
                  setCurrentIndex(
                    currentIndex === 0
                      ? currentImages.length - 1
                      : currentIndex - 1
                  )
                }
              >
                <FaChevronLeft size={18} />
              </button>
              <button
                className="bg-gray-800 text-white p-3 rounded-full hover:bg-gray-700 shadow-md transition-all"
                onClick={() =>
                  setCurrentIndex(
                    currentIndex === currentImages.length - 1
                      ? 0
                      : currentIndex + 1
                  )
                }
              >
                <FaChevronRight size={18} />
              </button>
            </div>

            {/* Counter */}
            <p className="text-center text-sm text-gray-500 mt-2">
              {currentIndex + 1} / {currentImages.length}
            </p>

            {/* Thumbnails */}
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              {currentImages.map((preview, index) => (
                <img
                  key={index}
                  src={preview}
                  alt={`Preview ${index}`}
                  className={`w-16 h-16 object-cover rounded-lg cursor-pointer transition-transform duration-200 
              ${
                index === currentIndex
                  ? "border-4 border-blue-500 scale-105"
                  : "border-gray-300 hover:scale-110"
              }`}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enquiry;
